import React, { useState } from 'react';
import { AlertTriangle, Send, FileText, User, CreditCard, Shield, MessageSquare } from 'lucide-react';
import { Input, TextArea, Button, SearchSelect } from '../../components/shared';

const ReportProblem = () => {
  const [activeTab, setActiveTab] = useState('booking');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    problemType: 'booking',
    bookingId: '',
    vendorName: '',
    subject: '',
    description: '',
    attachments: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tabs = [
    { id: 'booking', label: 'Booking Issue', icon: FileText },
    { id: 'payment', label: 'Payment Problem', icon: CreditCard },
    { id: 'vendor', label: 'Vendor Issue', icon: User },
    { id: 'safety', label: 'Safety Concern', icon: Shield },
    { id: 'other', label: 'Other', icon: MessageSquare },
  ];

  const problemTypes = {
    booking: [
      { value: 'cancellation', label: 'Cancellation Issue' },
      { value: 'no-show', label: 'Vendor No-Show' },
      { value: 'service-quality', label: 'Service Quality' },
      { value: 'booking-error', label: 'Booking Error' },
      { value: 'refund', label: 'Refund Request' },
    ],
    payment: [
      { value: 'charge-error', label: 'Incorrect Charge' },
      { value: 'refund-delay', label: 'Refund Delay' },
      { value: 'payment-failed', label: 'Payment Failed' },
      { value: 'billing-error', label: 'Billing Error' },
    ],
    vendor: [
      { value: 'communication', label: 'Communication Issue' },
      { value: 'unprofessional', label: 'Unprofessional Behavior' },
      { value: 'contract-violation', label: 'Contract Violation' },
      { value: 'false-advertising', label: 'False Advertising' },
    ],
    safety: [
      { value: 'harassment', label: 'Harassment' },
      { value: 'safety-concern', label: 'Safety Concern' },
      { value: 'inappropriate', label: 'Inappropriate Behavior' },
      { value: 'fraud', label: 'Fraud or Scam' },
    ],
    other: [
      { value: 'technical', label: 'Technical Issue' },
      { value: 'account', label: 'Account Problem' },
      { value: 'general', label: 'General Inquiry' },
    ],
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProblemTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      problemType: value,
      subject: '',
    }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setFormData(prev => ({
      ...prev,
      problemType: tabId,
      subject: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Thank you for reporting this issue. We\'ll investigate and get back to you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        problemType: activeTab,
        bookingId: '',
        vendorName: '',
        subject: '',
        description: '',
        attachments: [],
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white py-12 sm:py-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Report a Problem
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300">
              We're here to help resolve any issues you've encountered
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Tabs Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-black border-black"
                          : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                      <span className="hidden xs:inline sm:hidden md:inline">{tab.label}</span>
                      <span className="xs:hidden sm:inline md:hidden">{tab.label.split(' ')[0]}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="p-3 sm:p-4 md:p-6 lg:p-8">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                {/* Contact Information */}
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Your Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="text-sm sm:text-base"
                        required
                      />
                    </div>
                  </div>
                  <div className="mt-3 sm:mt-4 md:mt-6">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="text-sm sm:text-base"
                    />
                  </div>
                </div>

                {/* Problem Details */}
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                    Problem Details
                  </h3>
                  <div className="space-y-3 sm:space-y-4 md:space-y-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Problem Type <span className="text-red-500">*</span>
                      </label>
                      <SearchSelect
                        name="subject"
                        value={formData.subject}
                        onChange={handleProblemTypeChange}
                        options={problemTypes[activeTab] || []}
                        placeholder="Select problem type"
                        className="text-sm sm:text-base"
                        required
                      />
                    </div>

                    {activeTab === 'booking' && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          Booking ID
                        </label>
                        <Input
                          name="bookingId"
                          value={formData.bookingId}
                          onChange={handleChange}
                          placeholder="Enter booking ID if available"
                          className="text-sm sm:text-base"
                        />
                      </div>
                    )}

                    {(activeTab === 'vendor' || activeTab === 'booking') && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          Vendor Name
                        </label>
                        <Input
                          name="vendorName"
                          value={formData.vendorName}
                          onChange={handleChange}
                          placeholder="Name of the vendor"
                          className="text-sm sm:text-base"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <TextArea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Please provide a detailed description of the problem..."
                        rows="5"
                        className="text-sm sm:text-base"
                        required
                      />
                      <p className="mt-1.5 sm:mt-2 text-xs text-gray-500">
                        Include dates, times, and any relevant details that will help us investigate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex items-center justify-end pt-3 sm:pt-4 border-t border-gray-200">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    className="w-full sm:w-auto text-sm sm:text-base"
                  >
                    <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                    Submit Report
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 sm:p-4 md:p-6">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm sm:text-base md:text-lg font-semibold text-yellow-900 mb-1">
                  Important Information
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-yellow-800 mb-2 sm:mb-3">
                  We take all reports seriously and investigate them promptly. For urgent safety concerns, please contact emergency services (911) immediately, then call our emergency support line at 1-800-VENDA-911.
                </p>
                <p className="text-xs sm:text-sm text-yellow-800">
                  Response time: We typically respond to reports within 24 hours. For safety concerns, we respond within 4 hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportProblem;

